import {
  InputCurrency,
  InputNumber,
  Label,
  Radio,
  Select,
  Text,
} from '@mercai/clever'
import { useTranslation } from 'react-i18next'

import type { POSupplierProductFormProps } from './po-additional-product-form.type'

import { MERCAI_WAREHOUSES_OWNER_EMAIL, DECIMAL_PLACES_DEFAULT } from '@/constants'
import { useDollarQuotationLatest, useSupplierWarehouseList } from '@/services'
import { ProductQualityCategoryEnum } from '@/types'
import styles from './po-additional-product-form.module.css'

export const POSupplierProductForm = ({
  onChangeValues,
  values,
  errors,
  selectCatalogProduct,
  supplierUuid,
  currency,
}: POSupplierProductFormProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('componentPOAdditionalProductForm')

  const { data: dollarQuotation } = useDollarQuotationLatest()

  const onHandleValues = (key: string, value: string | number | boolean) => {
    if (key === 'deliveryOption') {
      return onChangeValues({
        ...values,
        deliveryOption: value as
          | 'keep_at_supplier'
          | 'deliver_to_our_warehouse',
        supplierWarehouseUuid: '',
      })
    }

    onChangeValues({ ...values, [key]: value })
  }

  const { data: warehouses } = useSupplierWarehouseList({
    params: {
      ...(values?.deliveryOption === 'deliver_to_our_warehouse' && {
        supplier__email: MERCAI_WAREHOUSES_OWNER_EMAIL,
      }),
      ...((!values?.deliveryOption ||
        values?.deliveryOption === 'keep_at_supplier') && {
        supplier__uuid: supplierUuid || '',
      }),
    },
  })

  return (
    <>
      {selectCatalogProduct && (
        <div className={styles.product}>
          <Text variant="title-base" className={styles['product-title']}>
            {selectCatalogProduct?.attributes?.title}
          </Text>

          <Text>
            <strong>{t('categoryLabel')}:</strong>{' '}
            {selectCatalogProduct?.category?.name || '-'}
          </Text>

          <Text>
            <strong>SKU:</strong> {selectCatalogProduct?.reference || '-'}
          </Text>

          {selectCatalogProduct?.attributes?.density && (
            <Text>
              <strong>{t('densityLabel')}:</strong>{' '}
              {selectCatalogProduct?.attributes?.density || '-'} g/cm³
            </Text>
          )}

          {selectCatalogProduct?.attributes?.fluidity && (
            <Text>
              <strong>{t('fluidityLabel')}:</strong>{' '}
              {selectCatalogProduct?.attributes?.fluidity || '-'} g/10min
            </Text>
          )}
        </div>
      )}
      <div className={styles.core}>
        <Text className={styles.title} variant="title-medium">
          {t('title')}
        </Text>
        <div>
          <InputCurrency
            title={t('costLabel')}
            required
            decimalPrecision={DECIMAL_PLACES_DEFAULT}
            exchangeRate={dollarQuotation?.data?.value || 1}
            primaryCurrency={currency}
            secondaryCurrency={currency === 'USD' ? 'MXN' : 'USD'}
            value={values?.cost}
            error={errors?.cost as string}
            onChange={(newValue) => onHandleValues('cost', newValue)}
          />
        </div>

        <div>
          <Label required>{t('quantityLabel')}</Label>
          <InputNumber
            showIcons
            placeholder={'0 Kg'}
            decimalPrecision={0}
            value={values?.quantity}
            error={errors?.quantity as string}
            onChange={(newValue) => onHandleValues('quantity', newValue)}
          />
        </div>

        <div>
          <Label>{t('categoryLabel')}</Label>
          <Select
            value={values?.category}
            error={errors?.category as string}
            onChange={(newValue) => onHandleValues('category', newValue)}
            options={[
              {
                label: t('selectEmpty'),
                value: '',
              },
              ...Object.keys(ProductQualityCategoryEnum).map((key) => ({
                label: tEnums(`product_quality_category_${key}`),
                value: key,
              })),
            ]}
          />
        </div>

        <div>
          <Label required>{t('deliveryOptionLabel')}</Label>
          <Radio
            value={values?.deliveryOption === 'keep_at_supplier'}
            onChange={() =>
              onHandleValues('deliveryOption', 'keep_at_supplier')
            }
          >
            {t('deliveryOptionsKeepAtSupplierLabel')}
          </Radio>
          <Radio
            value={values?.deliveryOption === 'deliver_to_our_warehouse'}
            onChange={() =>
              onHandleValues('deliveryOption', 'deliver_to_our_warehouse')
            }
          >
            {t('deliveryOptionsDeliverToOurWarehouseLabel')}
          </Radio>
          {errors?.deliveryOption && (
            <Text className={styles.error}>
              {errors?.deliveryOption as string}
            </Text>
          )}
        </div>

        <div>
          <Label>{t('warehouseLabel')}</Label>
          <Select
            placeholder={t('warehousePlaceholder')}
            value={values?.supplierWarehouseUuid}
            error={errors?.supplierWarehouseUuid as string}
            onChange={(newValue) =>
              onHandleValues('supplierWarehouseUuid', newValue)
            }
            options={[
              {
                label: t('warehouseEmptyLabel'),
                value: '',
              },
            ].concat(
              warehouses?.data?.results?.map((warehouse) => ({
                label: warehouse?.name || '',
                value: warehouse?.uuid || '',
              })) || [],
            )}
          />
        </div>
      </div>
    </>
  )
}
